import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  MapPin, 
  Search, 
  Loader, 
  Globe, 
  AlertCircle, 
  CheckCircle,
  ExternalLink,
  MapPinned
} from 'lucide-react';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { 
  GOOGLE_MAPS_API_KEY, 
  getGooglePlaceId, 
  getPlaceDetails,
  getPlacePhotoUrls,
  loadGoogleMapsScript
} from '../../utils/mapUtils';

// Custom styled autocomplete component to replace native Google Places UI
const StyledAutocomplete = ({ inputRef, onPlaceSelect, address, setAddress, isVerified, isSaving }) => {
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);
  const dropdownRef = useRef(null);
  
  // Initialize autocomplete service
  useEffect(() => {
    if (!window.google?.maps?.places) return;
    
    // Create a new session token
    if (!sessionToken.current && window.google.maps.places.AutocompleteSessionToken) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    
    // Initialize the autocomplete service
    if (!autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    
    // Clean up the dropdown when component unmounts
    return () => {
      setPredictions([]);
      sessionToken.current = null;
    };
  }, []);
  
  // Handle input changes and fetch predictions
  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    
    if (!value || value.length < 3 || !autocompleteService.current) {
      setPredictions([]);
      return;
    }
    
    setIsLoading(true);
    
    const request = {
      input: value,
      types: ['address'],
      sessionToken: sessionToken.current
    };
    
    autocompleteService.current.getPlacePredictions(
      request,
      (results, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
        } else {
          setPredictions([]);
        }
      }
    );
  };
  
  // Handle place selection
  const handlePredictionClick = (placeId, description) => {
    if (isSaving) return;
    
    setAddress(description);
    setPredictions([]);
    
    // Call the parent component's place selection handler
    if (onPlaceSelect) {
      onPlaceSelect(placeId);
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && 
          inputRef.current && !inputRef.current.contains(e.target)) {
        setPredictions([]);
        setIsFocused(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Limit predictions to 3 results for display
  const limitedPredictions = predictions.slice(0, 3);
  
  return (
    <div className="relative w-full">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        ref={inputRef}
        type="text"
        value={address}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        placeholder="Type and select an address from the dropdown"
        className={`w-full border ${isFocused ? 'border-black/70 ring-2 ring-black/10' : 'border-gray-200'} 
          rounded-xl h-10 sm:h-11 pl-9 pr-3 sm:pl-10 sm:pr-4 text-xs sm:text-sm 
          focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/70
          placeholder:text-gray-400 transition-all duration-200`}
        disabled={isSaving}
        aria-label="Search for an address"
        autoComplete="off"
      />
      
      {/* Custom autocomplete dropdown */}
      <AnimatePresence>
        {predictions.length > 0 && isFocused && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: 8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 8, height: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute z-[10002] mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          >
            {limitedPredictions.map((prediction) => (
              <motion.div
                key={prediction.place_id}
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.15 }}
                whileHover={{ backgroundColor: '#f9fafb' }}
                className="px-3 py-2.5 cursor-pointer flex items-start hover:bg-gray-50 border-b border-gray-50 last:border-0"
                onClick={() => handlePredictionClick(prediction.place_id, prediction.description)}
              >
                <MapPinned size={14} className="text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                <div className="flex flex-col">
                  <span className="text-sm text-gray-800">{prediction.structured_formatting?.main_text || prediction.description}</span>
                  {prediction.structured_formatting?.secondary_text && (
                    <span className="text-xs text-gray-500 mt-0.5">{prediction.structured_formatting.secondary_text}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
        
        {isLoading && isFocused && address.length >= 3 && predictions.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="absolute z-[10002] mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-100 p-4 flex justify-center"
          >
            <Loader size={16} className="text-gray-400 animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AddressEditPopup = ({ location, onClose, supabase, onSuccess }) => {
  const [address, setAddress] = useState(location.google_address || '');
  const [placeId, setPlaceId] = useState(location.google_places_id || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(!!location.google_places_id);
  const [errorMessage, setErrorMessage] = useState('');
  const [placeDetails, setPlaceDetails] = useState(null);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [companyName, setCompanyName] = useState('');
  
  const inputRef = useRef(null);
  const placesService = useRef(null);
  const popupRef = useRef(null);
  
  // Fetch company name when component mounts
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        // Fetch company name from vapi_call_settings
        const { data, error } = await supabase
          .from('vapi_call_settings')
          .select('company_name')
          .eq('account_id', user.id)
          .single();
          
        if (error) throw error;
        
        if (data && data.company_name) {
          setCompanyName(data.company_name);
        }
      } catch (err) {
        console.error('Error fetching company name:', err);
        // Fallback to location name if we can't get company name
        setCompanyName(location.name);
      }
    };
    
    fetchCompanyName();
  }, [supabase, location.name]);
  
  // Initialize Google Maps and Places Service
  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        await loadGoogleMapsScript();
        
        // Initialize the Places service for getting place details
        if (window.google?.maps) {
          // Create an invisible div to attach the PlacesService to
          const mapDiv = document.createElement('div');
          mapDiv.style.display = 'none';
          document.body.appendChild(mapDiv);
          
          const map = new window.google.maps.Map(mapDiv);
          placesService.current = new window.google.maps.places.PlacesService(map);
        }
      } catch (error) {
        console.error("Error loading Google Maps:", error);
        setErrorMessage("Failed to load Google Maps. Please try again later.");
      }
    };
    
    initializeGoogleMaps();
    
    // Clean up
    return () => {
      const mapDiv = document.querySelector('div[style="display: none;"]');
      if (mapDiv) {
        document.body.removeChild(mapDiv);
      }
    };
  }, []);
  
  // If there's an existing place ID, fetch its details on load
  useEffect(() => {
    if (location.google_places_id && !placeDetails) {
      fetchPlaceDetails(location.google_places_id);
    }
  }, [location.google_places_id]);
  
  // Fetch place details to get photos
  const fetchPlaceDetails = async (placeId) => {
    if (!placeId) return;
    
    setIsLoadingPhotos(true);
    
    try {
      const data = await getPlaceDetails(placeId);
      
      if (data) {
        setPlaceDetails(data);
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    } finally {
      setIsLoadingPhotos(false);
    }
  };
  
  // Handle place selection from our custom autocomplete
  const handlePlaceSelect = async (selectedPlaceId) => {
    if (!selectedPlaceId) return;
    
    setIsVerifying(true);
    setErrorMessage('');
    
    try {
      setPlaceId(selectedPlaceId);
      
      // Get place details to verify the address
      if (placesService.current) {
        placesService.current.getDetails(
          {
            placeId: selectedPlaceId,
            fields: ['formatted_address', 'geometry']
          },
          (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
              // Update the address with the formatted one from Google
              if (place.formatted_address) {
                setAddress(place.formatted_address);
              }
              
              setIsVerified(true);
              fetchPlaceDetails(selectedPlaceId);
            } else {
              console.error('Error getting place details:', status);
              setErrorMessage('Failed to verify this address');
              setIsVerified(false);
            }
            setIsVerifying(false);
          }
        );
      } else {
        // Fallback to the direct API method if Places Service isn't available
        const data = await getPlaceDetails(selectedPlaceId);
        if (data && data.formattedAddress) {
          setAddress(data.formattedAddress);
          setIsVerified(true);
          setPlaceDetails(data);
        } else {
          setErrorMessage('Failed to verify this address');
          setIsVerified(false);
        }
        setIsVerifying(false);
      }
    } catch (error) {
      console.error('Error selecting place:', error);
      setErrorMessage('Failed to verify this address');
      setIsVerified(false);
      setIsVerifying(false);
    }
  };
  
  // Handle backdrop click to prevent closing when clicking inside
  const handleBackdropClick = (e) => {
    // Only close if clicking directly on the backdrop
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Save the updated address to the database
  const handleSave = async () => {
    if (!isVerified || !placeId) {
      setErrorMessage('Please select a verified address from the dropdown');
      return;
    }
    
    setIsSaving(true);
    
    try {
      // 1. First update the database with the new address and place ID
      const { error: dbError } = await supabase
        .from('vapi_location_settings')
        .update({ 
          google_address: address,
          google_places_id: placeId
        })
        .eq('id', location.id);
      
      if (dbError) throw dbError;
      
      // 2. Then send a request to the backend endpoint
      const backendResponse = await fetch('https://cleaner-automation-936dbbf97f9e.herokuapp.com/api/locations/context/new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ location_id: location.id }),
      });
      
      // Check if backend request was successful
      if (!backendResponse.ok) {
        // We still updated the DB, so consider this a partial success
        console.error('Backend request failed:', await backendResponse.text());
        toast.warning('Address saved but may take a moment to update in your AI assistant');
        
        // Still call onSuccess to refresh the location data
        onSuccess();
        onClose();
        return;
      }
      
      // 3. If we reach here, everything was successful
      toast.success('Address updated successfully');
      onSuccess();
      onClose();
      
    } catch (err) {
      console.error('Error saving address:', err);
      toast.error('Failed to save address');
      setErrorMessage('Failed to save address');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };
  
  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };
  
  // Portal the modal to ensure it's at the root level of the DOM
  const popupContent = (
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={handleBackdropClick}
      >
        <motion.div
          ref={popupRef}
          variants={popupVariants}
          className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
          onClick={e => e.stopPropagation()} // Prevent event propagation
        >
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b sticky top-0 bg-white z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                <MapPin size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
              </div>
              <div>
                <h2 className="font-medium text-base sm:text-lg text-gray-900">Update Address</h2>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                  Edit the address for "{location.name}"
                </p>
              </div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={16} className="sm:w-[18px] sm:h-[18px]" />
            </motion.button>
          </motion.div>
          
          {/* Content */}
          <div className="p-5 sm:p-7 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
            {/* Address input with custom Google Places */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-3 sm:space-y-4"
            >
              <div className="space-y-1.5">
                <label htmlFor="address" className="text-sm font-medium text-gray-700">
                  Business Address
                </label>
                
                {/* Replace the standard input with our styled autocomplete */}
                <StyledAutocomplete 
                  inputRef={inputRef}
                  onPlaceSelect={handlePlaceSelect}
                  address={address}
                  setAddress={setAddress}
                  isVerified={isVerified}
                  isSaving={isSaving}
                />
                
                <p className="text-[10px] sm:text-xs text-gray-500">
                  Type your address and select it from the dropdown suggestions
                </p>
              </div>
              
              {/* Verification status */}
              <div className="flex flex-col space-y-2">
                {isVerifying && (
                  <div className="flex items-center text-blue-600 text-xs">
                    <Loader className="w-3.5 h-3.5 mr-2 animate-spin" />
                    Verifying address...
                  </div>
                )}
                
                {isVerified && !isVerifying && placeId && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-3 sm:p-4 bg-emerald-50 border border-emerald-100 rounded-xl"
                  >
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <div className="ml-2 sm:ml-3 space-y-2 w-full">
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-emerald-700">
                            Address verified with Google Maps
                          </p>
                          
                          {placeDetails?.websiteUri && (
                            <a 
                              href={placeDetails.websiteUri} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[10px] sm:text-xs text-blue-600 flex items-center mt-0.5 hover:underline"
                            >
                              <ExternalLink size={10} className="mr-1" />
                              View business website
                            </a>
                          )}
                        </div>
                        
                        {/* Loading state for place details - keep this for consistency */}
                        {isLoadingPhotos && (
                          <div className="flex items-center justify-center py-3 w-full">
                            <Loader className="w-4 h-4 text-emerald-600 animate-spin" />
                            <span className="ml-2 text-xs text-emerald-600">Verifying address...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
                
                {errorMessage && !isVerifying && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center text-red-600 text-xs p-3 sm:p-4 bg-red-50 border border-red-100 rounded-xl"
                  >
                    <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                    {errorMessage}
                  </motion.div>
                )}
              </div>
              
              {/* Google verification explanation */}
              <div className="p-3 sm:p-4 bg-gray-50 rounded-xl space-y-2">
                <div className="flex items-center text-xs sm:text-sm font-medium text-gray-700">
                  <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 text-gray-500" />
                  Why verify with Google?
                </div>
                <p className="text-[10px] sm:text-xs text-gray-600">
                  Google verification ensures accurate location data for your AI assistant. 
                  It helps customers find your business more easily and improves the accuracy of directions.
                </p>
              </div>
            </motion.div>
          </div>
          
          {/* Footer */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="px-5 sm:px-7 py-4 bg-gray-50 flex justify-end gap-2 sm:gap-3 sticky bottom-0 border-t"
          >
            <motion.button
              whileHover={{ scale: 1.02, backgroundColor: '#f9fafb' }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl border border-gray-200 text-gray-700 text-xs sm:text-sm font-medium
                bg-white hover:bg-gray-50 transition-colors"
              disabled={isSaving}
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={isSaving || !isVerified || !placeId}
              className="py-2 sm:py-2.5 px-4 sm:px-5 rounded-xl bg-black text-white text-xs sm:text-sm font-medium
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="flex items-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="mr-2 h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  Saving...
                </div>
              ) : (
                "Save Address"
              )}
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
  
  // Use a portal to render the modal at the document level
  return createPortal(popupContent, document.body);
};

export default AddressEditPopup; 